import { Units } from './types';

const numberRangeRegExp = () => {
  const separatorExp = '[-–—]|to';
  const numberOrFractionExp = '(?:[0-9]* ?[0-9]+/[0-9]+)|[0-9][\'"][0-9]?"?|[0-9]+';
  return new RegExp(`(${numberOrFractionExp}) ?(${separatorExp}) ?(${numberOrFractionExp})`);
};

// Separates strings with numbers into array of string and/or number
// feet, inches, fractions converted to decimal
// e.g., "10 - 20" -> ["10", "-", "20]
export const toTokensNormalised = (value: string): (string | number)[] => {
  return value
    .replace(numberRangeRegExp(), '$1_$2_$3')
    .split('_')
    .map((val) => {
      if (val.match(/[a-z]/i)) return val;
      // feet and/or inches, e.g., 5', 5'10, 34"
      if (val.match(/["']/g)) return feetAndInchesToDecimal(val);
      // fraction e.g., "1 1/12"
      if (val.includes('/')) return fractionToDecimal(val);
      const number = parseFloat(val);
      return isNaN(number) ? val : number;
    });
};

const decimalToFraction = (value: number): string => {
  const integer = Math.floor(value);
  const fractional = value % 1;
  const step = 0.125; // 1/8th inch precision
  const snappedFractional = Math.round(fractional / step) * step;

  if (snappedFractional === 0.125) return `${integer} 1/8`;
  if (snappedFractional === 0.25) return `${integer} 1/4`;
  if (snappedFractional === 0.375) return `${integer} 3/8`;
  if (snappedFractional === 0.5) return `${integer} 1/2`;
  if (snappedFractional === 0.625) return `${integer} 5/8`;
  if (snappedFractional === 0.75) return `${integer} 3/4`;
  if (snappedFractional === 0.875) return `${integer} 7/8`;

  return Math.round(value).toString();
};

const feetAndInchesToDecimal = (value: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, feet, inches] = value.match(/([0-9]+')?([0-9]+")?/) ?? [];
  // eslint-disable-next-line no-mixed-operators
  return parseInt(feet ?? 0, 10) * 12 + parseInt(inches ?? 0, 10);
};

const fractionToDecimal = (value: string) => {
  const [int, frac] = value.split(' ');
  const fractional =
    frac === '1/8'
      ? '.125'
      : frac === '1/4'
      ? '.25'
      : frac === '3/8'
      ? '.375'
      : frac === '1/2'
      ? '.5'
      : frac === '5/8'
      ? '.675'
      : frac === '3/4'
      ? '.75'
      : frac === '7/8'
      ? '.875'
      : '';

  return parseFloat(`${int}${fractional}`);
};

export const convert =
  (from: Units | undefined, to: Units | undefined) => (value: number | string) => {
    if (typeof value !== 'number' || !from || !to) return value;
    if (to === Units.INCH) return value / 2.54;
    if (to === Units.CM) return value * 2.54;
    return value;
  };

export const format = (units: Units | undefined) => (value: string | number) => {
  if (typeof value === 'string') return value;
  if (units === Units.INCH) return decimalToFraction(value);
  if (units === Units.CM) return value.toFixed(1).replace('.0', '');
  return value;
};
